import $http from '../services/AxiosInstance';
import { saveAs } from 'file-saver';

export interface OperationSearchData {
  search?: string | null;
  companyId?: number | null;
  agentId?: number | null;
  clientId?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface Params {
  page?: number;
  searchData?: OperationSearchData | null;
}

interface DownloadParams {
  clientId: number;
  policyId: number;
  documentPath?: string;
  policyNumberId?: number;
}

type ITypePolicy = 'terrestre' | 'maritimo' | 'paqueteria';

class OperationsRepository {
  async getOperations(params: Params) {
    try {
      let url = `/clients/policies?page=${params.page}`;

      if (params.searchData !== null) {
        if (!!params.searchData?.search && params.searchData?.search.trim() !== '') {
          url += `&search=${params.searchData?.search ?? ''}`;
        }
        if (!!params.searchData?.companyId) {
          url += `&companyId=${params.searchData.companyId}`;
        }
        if (!!params.searchData?.agentId) {
          url += `&agentId=${params.searchData.agentId}`;
        }
        if (!!params.searchData?.clientId) {
          url += `&clientId=${params.searchData.clientId}`;
        }
        if (!!params.searchData?.startDate) {
          url += `&startDate=${params.searchData.startDate}`;
        }
        if (!!params.searchData?.endDate) {
          url += `&endDate=${params.searchData.endDate}`;
        }
      }

      const response = await $http.get(url);
      return response;
    } catch (errors: any) {
      return errors;
    }
  }
  async getOperationsMaritimes(params: Params) {
    try {
      let url = `/clients/policies-maritimes?page=${params.page}`;

      if (params.searchData !== null) {
        if (!!params.searchData?.search && params.searchData?.search.trim() !== '') {
          url += `&search=${params.searchData?.search ?? ''}`;
        }
        if (!!params.searchData?.companyId) {
          url += `&companyId=${params.searchData.companyId}`;
        }
        if (!!params.searchData?.agentId) {
          url += `&agentId=${params.searchData.agentId}`;
        }
        if (!!params.searchData?.clientId) {
          url += `&clientId=${params.searchData.clientId}`;
        }
        if (!!params.searchData?.startDate) {
          url += `&startDate=${params.searchData.startDate}`;
        }
        if (!!params.searchData?.endDate) {
          url += `&endDate=${params.searchData.endDate}`;
        }
      }

      const response = await $http.get(url);
      return response;
    } catch (errors: any) {
      return errors;
    }
  }
  async getOperationsPackages(params: Params) {
    try {
      let url = `/clients/policies-packages?page=${params.page}`;

      if (params.searchData !== null) {
        if (!!params.searchData?.search && params.searchData?.search.trim() !== '') {
          url += `&search=${params.searchData?.search ?? ''}`;
        }
        if (!!params.searchData?.companyId) {
          url += `&companyId=${params.searchData.companyId}`;
        }
        if (!!params.searchData?.agentId) {
          url += `&agentId=${params.searchData.agentId}`;
        }
        if (!!params.searchData?.clientId) {
          url += `&clientId=${params.searchData.clientId}`;
        }
        if (!!params.searchData?.startDate) {
          url += `&startDate=${params.searchData.startDate}`;
        }
        if (!!params.searchData?.endDate) {
          url += `&endDate=${params.searchData.endDate}`;
        }
      }

      const response = await $http.get(url);
      return response;
    } catch (errors: any) {
      return errors;
    }
  }

  async getExcelPolicies({ typePolicy, searchData, toExcel }: {
    typePolicy: ITypePolicy;
    toExcel: boolean;
    searchData?: OperationSearchData | null;
  }) {

    const dictionaryTypes = {
      terrestre: '/clients/policies',
      maritimo: '/clients/policies-maritimes',
      paqueteria: '/clients/policies-packages',
    }

    async function getExcel(url: string, params: any) {
      const headers = { ...params.headers };
      delete headers['Content-Type'];

      const config = {
        headers,
        params,
        responseType: 'blob' as any,
      };

      if (params) {
        config.params = params;
      }

      return await $http.get(url, config);
    }
    try {

      const params = new URLSearchParams();

      params.append('toExcel', String(toExcel));
      if (searchData?.search && searchData?.search.trim() !== '') {
        params.append('search', String(searchData.search));
      }
      if (searchData?.companyId) {
        params.append('companyId', String(searchData.companyId));
      }
      if (searchData?.agentId) {
        params.append('agentId', String(searchData.agentId));
      }
      if (searchData?.clientId) {
        params.append('clientId', String(searchData.clientId));
      }
      if (searchData?.startDate) {
        params.append('startDate', searchData.startDate);
      }
      if (searchData?.endDate) {
        params.append('endDate', searchData.endDate);
      }

      const { data } = await getExcel(dictionaryTypes[typePolicy], params);

      saveAs(data, `Operaciones_${typePolicy}.xlsx`);
    }
    catch (error) {
      return error;
    }
  }
  async getOperationPackageNumbersGuides(id: number) {
    try {
      const response = await $http.get(`/clients/policies-packages/number-guides/${id}`);
      return response;
    } catch (errors: any) {
      return errors;
    }
  }
  async createClientInChubb(clientId: number) {
    try {
      const response = await $http.post(
        `/clients/createChubbPerson/${clientId}`,
      );
      return response;
    } catch (errors: any) {
      throw errors;
    }
  }
  async downloadClientDocument(params: DownloadParams) {
    const { clientId, policyId, documentPath } = params;

    try {
      const { data } = await $http.get(
        `/images/download/${clientId}/${policyId}/document/document-client`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([data]);

      saveAs(blob, `doc-${policyId}.${documentPath?.split('.')[1]}`);
      return 'ok';
    } catch (errors: any) {
      return null;
    }
  }
  async quotePolicy(policyId: number) {
    try {
      const { data } = await $http.post(`clients/policies/${policyId}/quote`);
      return data;
    } catch (errors: any) {
      throw errors;
    }
  }
  async getPaymentURL(policyId: number) {
    try {
      const { data } = await $http.get(
        `clients/policies/${policyId}/paymentURL`,
      );
      return data;
    } catch (errors: any) {
      throw new Error();
    }
  }
  async createIssuePolicy(policyId: number) {
    try {
      const { data } = await $http.post(`clients/policies/${policyId}/issue`);
      return data;
    } catch (errors: any) {
      throw errors;
    }
  }
  async getPDFPolicy(policyId: number) {
    try {
      const { data } = await $http.get(`clients/policies/${policyId}/chubbpdf`);
      return data;
    } catch (errors: any) {
      throw errors;
    }
  }
  async downloadPDFPolicy(params: DownloadParams) {
    const { clientId, policyId, policyNumberId } = params;

    try {
      const { data } = await $http.get(
        `/images/download/${clientId}/${policyId}/document/chubb-document`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([data]);

      saveAs(blob, `${policyNumberId}.pdf`);
    } catch (errors: any) {
      throw new Error();
    }
  }
  async solvedManually(policyId: number) {
    try {
      const { data } = await $http.get(
        `/clients/policies/${policyId}/manuallySolved`,
      );

      return data;
    } catch (errors: any) {
      throw errors;
    }
  }
  async sendPDFtoClient(policyId: number) {
    try {
      const { data } = await $http.get(
        `/clients/policies/${policyId}/sendChubbpdf`,
      );

      return data;
    } catch (errors: any) {
      throw errors;
    }
  }

  async getDateLastSendPolicyToClient(policyId: number){{
    if(!policyId) return;
    try {
      const { data } = await $http.get(
        `/clients/date-email-to-client/${policyId}`,
      );
      return data;
    } catch (errors: any) {
      throw new Error();
    }
  }}
}

export default OperationsRepository;
